import _jsx from '@babel/runtime/helpers/jsx';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import React, { useState, useEffect, useReducer, useCallback } from 'react';
import 'prop-types';
import { Button, ButtonText } from '@andes/button';
import { Checkbox } from '@andes/checkbox';
import classnames from 'classnames';
import { Popper } from '@andes/popper';
import { FocusScope } from '@react-aria/focus';
import { TextField } from '@andes/textfield';
import { MoneyAmount } from '@andes/money-amount';
import { BottomSheet } from '@andes/bottom-sheet';
const Dialog = _ref => {
  let {
    children,
    modal,
    srLabel
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    "aria-label": srLabel,
    "aria-modal": modal,
    role: "dialog"
  }, void 0, /*#__PURE__*/_jsx(FocusScope, {
    contain: modal,
    restoreFocus: true,
    autoFocus: true
  }, void 0, children));
};
const namespace$2 = 'calculator-block-content';
const flipModifier = {
  options: {
    fallbackPlacements: ['bottom']
  }
};
const CalculatorContent = _ref => {
  let {
    open,
    onClose,
    children,
    trigger,
    srLabel,
    modal
  } = _ref;
  const handleClose = event => {
    if (typeof onClose === 'function') {
      onClose(event);
    }
  };
  const handleClickOutside = () => handleClose;
  const hideOnEscPlugin = {
    name: 'hideOnEsc',
    defaultValue: true,
    fn(_ref2) {
      let {
        popper
      } = _ref2;
      function localOnKeyDown(event) {
        if (event.key === 'Escape') {
          handleClose(event);
        }
      }
      return {
        onShow() {
          popper.addEventListener('keydown', localOnKeyDown);
        },
        onHide() {
          popper.removeEventListener('keydown', localOnKeyDown);
        }
      };
    }
  };
  return /*#__PURE__*/_jsx(Popper, {
    interactive: true,
    popperOptions: {
      modifiers: [{
        name: 'eventListeners',
        options: {
          scroll: false
        }
      }, _objectSpread({
        name: 'flip'
      }, flipModifier)]
    },
    placement: "bottom",
    trigger: "click",
    className: "".concat(namespace$2, "__calculator-dialog"),
    paddingSize: 16,
    visible: open,
    content: open && /*#__PURE__*/_jsx(Dialog, {
      srLabel: srLabel,
      modal: modal
    }, void 0, children),
    onClickOutside: handleClickOutside(),
    plugins: [hideOnEscPlugin]
  }, void 0, trigger);
};
var SortIcon = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTExLjU5MjMgMTQuODQzNUwxMC43NDM4IDEzLjk5NUwxMi41Mzg4IDEyLjJMMi4wMDc0OSAxMi4yTDIuMDA3NDkgMTFMMTIuNTYyOCAxMUwxMC43NTI4IDkuMTlMMTEuNjAxMyA4LjM0MTQ3TDE0Ljg0NzkgMTEuNTg4TDExLjU5MjMgMTQuODQzNVoiIGZpbGw9ImJsYWNrIiBmaWxsLW9wYWNpdHk9IjAuNTUiLz4KPHBhdGggZD0iTTQuNDAwNDEgNy42NTI1M0w1LjI0ODk0IDYuODA0TDMuNDUzOTQgNS4wMDlMMTQuMDY1MiA1LjAwOUwxNC4wNjUyIDMuODA5TDMuNDI5OTQgMy44MDlMNS4yMzk5NCAxLjk5OUw0LjM5MTQxIDEuMTUwNDdMMS4xNDQ4OSA0LjM5N0w0LjQwMDQxIDcuNjUyNTNaIiBmaWxsPSJibGFjayIgZmlsbC1vcGFjaXR5PSIwLjU1Ii8+Cjwvc3ZnPgo=";
const CalculatorInputs = _ref => {
  let {
    inputs,
    isError,
    inputYieldValue,
    handleYieldChange,
    handleInputFocus,
    inputUnitValue,
    handleUnitChange,
    namespace,
    isMobile = false
  } = _ref;
  const [lastFocused, setLastFocused] = React.useState(false);
  const handleFocus = (lastFocusedInput, value) => {
    setLastFocused(lastFocusedInput);
    handleInputFocus(value);
  };
  return /*#__PURE__*/_jsx("div", {
    className: classnames("".concat(namespace, "__inputs"))
  }, void 0, /*#__PURE__*/_jsx(TextField, {
    type: isMobile ? 'search' : undefined,
    inputmode: isMobile ? 'numeric' : undefined,
    autocomplete: "off",
    autoFocus: true,
    focus: isError && lastFocused === 'inputYield',
    placeholder: inputs.input_list[0].placeholder,
    className: classnames("".concat(namespace, "__inputs__yield")),
    value: inputYieldValue,
    onChange: handleYieldChange,
    onFocus: () => handleFocus('inputYield', false),
    label: isMobile ? inputs.input_list[0].input_title.text : undefined,
    srLabel: inputs.input_list[0].input_title.accessibility_text,
    modifier: isError ? 'error' : undefined
  }, void 0, !isMobile && /*#__PURE__*/_jsx("span", {
    className: classnames("".concat(namespace, "__input-suffix"))
  }, void 0, inputs.input_list[0].input_title.unit)), inputs.input_type !== 'SIMPLE_INPUT' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx("img", {
    src: SortIcon,
    alt: "Sort",
    className: classnames("".concat(namespace, "__inputs__sort-icon"))
  }), /*#__PURE__*/_jsx(TextField, {
    type: isMobile ? 'search' : undefined,
    inputmode: isMobile ? 'numeric' : undefined,
    autocomplete: "off",
    focus: isError && lastFocused === 'inputUnit',
    placeholder: inputs.input_list[1].placeholder,
    className: classnames("".concat(namespace, "__inputs__unit")),
    value: inputUnitValue,
    onChange: handleUnitChange,
    onFocus: () => handleFocus('inputUnit', false),
    label: isMobile ? inputs.input_list[1].input_title.text : undefined,
    srLabel: inputs.input_list[1].input_title.accessibility_text,
    modifier: isError ? 'error' : undefined
  }, void 0, !isMobile && /*#__PURE__*/_jsx("span", {
    className: classnames("".concat(namespace, "__input-suffix"))
  }, void 0, inputs.input_list[1].input_title.unit))));
};
var ErrorIcon = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBkPSJNNiAwTDYgMEE2IDYgMCAwIDEgMTIgNkwxMiA2QTYgNiAwIDAgMSA2IDEyTDYgMTJBNiA2IDAgMCAxIDAgNkwwIDZBNiA2IDAgMCAxIDYgMHoiIGZpbGw9InJlZCIvPgogIDxwYXRoIGQ9Ik02LjcyNyAyLjkwOUg1LjI3M0w1LjQ1NSA2LjkwOUg2LjU0NUw2LjcyNyAyLjkwOVoiIGZpbGw9IndoaXRlIi8+CiAgPHBhdGggZD0iTTYgNy42MzZDNi40MDIgNy42MzYgNi43MjcgNy45NjIgNi43MjcgOC4zNjRDNi43MjcgOC43NjUgNi40MDIgOS4wOTEgNiA5LjA5MUM1LjU5OCA5LjA5MSA1LjI3MyA4Ljc2NSA1LjI3MyA4LjM2NEM1LjI3MyA3Ljk2MiA1LjU5OCA3LjYzNiA2IDcuNjM2WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==";
var _img$1;
const ErrorMessage = _ref => {
  let {
    isError,
    errorMessage,
    namespace,
    className
  } = _ref;
  return isError && /*#__PURE__*/_jsx("div", {
    className: classnames(className, "".concat(namespace, "__error-message"))
  }, void 0, _img$1 || (_img$1 = /*#__PURE__*/_jsx("img", {
    src: ErrorIcon,
    alt: "Error"
  })), /*#__PURE__*/_jsx("span", {}, void 0, errorMessage));
};
const getPriceObject = function (value) {
  let currencyId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  const [fraction, cents] = value.toFixed(2).split('.');
  const isCommaSeparator = ['PEN', 'MXN'].includes(currencyId);
  const separator = isCommaSeparator ? ',' : '.';
  return {
    // eslint-disable-next-line security/detect-unsafe-regex
    fraction: fraction.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1".concat(separator)),
    cents: isCommaSeparator ? null : cents || '00'
  };
};
const formatYieldValue = function (yieldValue) {
  let countryCode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'es-AR';
  return new Intl.NumberFormat(countryCode).format(yieldValue);
};
const deformatStringValue = yieldValue => {
  if (typeof yieldValue === 'number') {
    return yieldValue;
  }
  return Number(yieldValue.replace(/[,.]/g, ''));
};
const Footer = _ref => {
  let {
    priceDescription,
    totalPrice,
    footer,
    inputYieldValue,
    inputUnitValue,
    isError,
    handleClose,
    namespace
  } = _ref;
  const [isBlinking, setIsBlinking] = useState(false);
  const [displayedValues, setDisplayedValues] = useState({
    priceDescription,
    totalPrice
  });
  useEffect(() => {
    if (inputYieldValue !== '0' && (priceDescription !== displayedValues.priceDescription || totalPrice !== displayedValues.totalPrice)) {
      setIsBlinking(true);
      const timer = setTimeout(() => {
        setDisplayedValues({
          priceDescription,
          totalPrice
        });
        setIsBlinking(false);
      }, 500);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [priceDescription, totalPrice, inputYieldValue, displayedValues.priceDescription, displayedValues.totalPrice]);
  return /*#__PURE__*/_jsx("div", {
    className: classnames("".concat(namespace, "__footer"))
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: classnames("".concat(namespace, "__footer__price"), {
      blinking: isBlinking
    })
  }, void 0, /*#__PURE__*/_jsx("span", {
    className: classnames("".concat(namespace, "__footer__price-description"))
  }, void 0, displayedValues.priceDescription), /*#__PURE__*/_jsx("div", {
    className: classnames("".concat(namespace, "__footer__price-total"))
  }, void 0, /*#__PURE__*/_jsx(MoneyAmount, {
    applyHideDecimals: displayedValues.totalPrice % 1 === 0,
    value: getPriceObject(displayedValues.totalPrice, footer.total_price.currency_id),
    centsType: footer.total_price.decimal_style.toLowerCase(),
    currencyId: footer.total_price.currency_id,
    size: 14,
    weight: "semibold"
  }))), /*#__PURE__*/_jsx(Button, {
    onClick: handleClose,
    className: classnames("".concat(namespace, "__footer__apply-button")),
    disabled: !inputYieldValue || !inputUnitValue || parseFloat(inputYieldValue.replace(',', '.')) === 0 || isError
  }, void 0, footer.confirm_button.text));
};
var REACT_ELEMENT_TYPE;
function _createRawReactElement(e, r, E, l) {
  REACT_ELEMENT_TYPE || (REACT_ELEMENT_TYPE = "function" == typeof Symbol && Symbol.for && Symbol.for("react.element") || 60103);
  var o = e && e.defaultProps,
    n = arguments.length - 3;
  if (r || 0 === n || (r = {
    children: void 0
  }), 1 === n) r.children = l;else if (n > 1) {
    for (var t = Array(n), f = 0; f < n; f++) t[f] = arguments[f + 3];
    r.children = t;
  }
  if (r && o) for (var i in o) void 0 === r[i] && (r[i] = o[i]);else r || (r = o || {});
  return {
    $$typeof: REACT_ELEMENT_TYPE,
    type: e,
    key: void 0 === E ? null : "" + E,
    ref: null,
    props: r,
    _owner: null
  };
}
const Asset$2 = _ref => {
  let {
    color = "rgba(0, 0, 0, 0.9)",
    i18n: i18n,
    srLabel: srLabel
  } = _ref;
  return _createRawReactElement("svg", {
    "aria-hidden": srLabel ? void 0 : !srLabel,
    "aria-label": srLabel || void 0,
    role: srLabel ? "img" : void 0,
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: color
  }, void 0, srLabel && _createRawReactElement("title", {}, void 0, srLabel), _createRawReactElement("path", {
    d: "M4.93237 9.00124L5.99303 7.94058L11.9968 13.9443L18.0068 7.93433L19.0674 8.99499L11.9968 16.0656L4.93237 9.00124Z",
    fill: color
  }));
};
const Asset$1 = _ref => {
  let {
    color = "rgba(0, 0, 0, 0.9)",
    i18n: i18n,
    srLabel: srLabel
  } = _ref;
  return _createRawReactElement("svg", {
    "aria-hidden": srLabel ? void 0 : !srLabel,
    "aria-label": srLabel || void 0,
    role: srLabel ? "img" : void 0,
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: color
  }, void 0, srLabel && _createRawReactElement("title", {}, void 0, srLabel), _createRawReactElement("path", {
    d: "M17.9941 16.0544L11.9903 10.0506L5.98034 16.0606L4.91968 15L11.9903 7.92932L19.0547 14.9937L17.9941 16.0544Z",
    fill: color
  }));
};
const Asset = _ref => {
  let {
    color = "rgba(0, 0, 0, 0.9)",
    i18n: i18n,
    srLabel: srLabel
  } = _ref;
  return _createRawReactElement("svg", {
    "aria-hidden": srLabel ? void 0 : !srLabel,
    "aria-label": srLabel || void 0,
    role: srLabel ? "img" : void 0,
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: color
  }, void 0, srLabel && _createRawReactElement("title", {}, void 0, srLabel), _createRawReactElement("path", {
    d: "M8.80264 5.99316L14.8064 11.9969L8.79639 18.0069L9.85705 19.0676L16.9277 11.9969L9.8633 4.9325L8.80264 5.99316Z",
    fill: color
  }));
};
var _ChevronUp, _ChevronRight, _ChevronDown;
const ButtonTriggerContent = _ref => {
  let {
    header,
    open,
    namespace,
    isMobile
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    className: classnames("".concat(namespace, "__trigger__text-container"))
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: classnames("".concat(namespace, "__trigger__text-wrapper"))
  }, void 0, /*#__PURE__*/_jsx(ButtonText, {}, void 0, header.title.text_regular), /*#__PURE__*/_jsx(ButtonText, {}, void 0, /*#__PURE__*/_jsx("span", {
    className: classnames("".concat(namespace, "__trigger__text-container__bold"))
  }, void 0, header.title.text_bold), /*#__PURE__*/_jsx("span", {
    className: classnames("".concat(namespace, "__trigger__text-container__units"))
  }, void 0, header.title.text_units))), /*#__PURE__*/_jsx("span", {
    className: classnames("".concat(namespace, "__trigger__chevron"))
  }, void 0, (() => {
    if (open) {
      return _ChevronUp || (_ChevronUp = /*#__PURE__*/_jsx(Asset$1, {}));
    }
    if (isMobile) {
      return _ChevronRight || (_ChevronRight = /*#__PURE__*/_jsx(Asset, {}));
    }
    return _ChevronDown || (_ChevronDown = /*#__PURE__*/_jsx(Asset$2, {}));
  })()));
};
ButtonTriggerContent.defaultProps = {
  isMobile: false
};
const calculatorInitialState = {
  open: false,
  filled: false,
  inputYieldValue: '',
  inputUnitValue: '',
  sale_unit_quantity: '',
  bulk_sale_quantity: '',
  unit_quantity_text: '',
  priceDescription: '',
  totalPrice: 0,
  isChecked: false,
  isError: false,
  isLocalError: false,
  isManualUnitChange: false,
  yieldOnApply: '',
  unitOnApply: '',
  checkedOnApply: false
};
const calculatorActionTypes = {
  TOGGLE_OPEN: 'TOGGLE_OPEN',
  CLOSE_MODAL: 'CLOSE_MODAL',
  SET_INPUT_YIELD: 'SET_INPUT_YIELD',
  SET_INPUT_UNIT: 'SET_INPUT_UNIT',
  SET_PRICE_DESCRIPTION: 'SET_PRICE_DESCRIPTION',
  SET_TOTAL_PRICE: 'SET_TOTAL_PRICE',
  SET_CHECKED: 'SET_CHECKED',
  SET_ERROR: 'SET_ERROR',
  SET_MANUAL_UNIT_CHANGE: 'SET_MANUAL_UNIT_CHANGE',
  RESET_CONTENT: 'RESET_CONTENT'
};
const calculatorReducer = (state, action) => {
  var _action$disableCheck;
  switch (action.type) {
    case calculatorActionTypes.CLOSE_MODAL:
      if (state.filled) {
        return _objectSpread(_objectSpread({}, state), {}, {
          inputYieldValue: state.yieldOnApply,
          inputUnitValue: state.unitOnApply,
          isChecked: state.checkedOnApply,
          open: false
        });
      }
      return _objectSpread(_objectSpread(_objectSpread({}, state), calculatorInitialState), {}, {
        totalPrice: state.totalPrice,
        priceDescription: state.priceDescription
      });
    case calculatorActionTypes.TOGGLE_OPEN:
      {
        if (!action.filled && !state.filled) {
          return _objectSpread(_objectSpread(_objectSpread({}, state), calculatorInitialState), {}, {
            totalPrice: state.totalPrice,
            priceDescription: state.priceDescription,
            open: !state.open,
            filled: action.filled || state.filled
          });
        }
        const updatedInputs = action.filled ? {
          yieldOnApply: action.yieldOnApply,
          unitOnApply: action.unitOnApply,
          checkedOnApply: action.checkedOnApply
        } : {
          yieldOnApply: state.yieldOnApply,
          unitOnApply: state.unitOnApply,
          checkedOnApply: state.checkedOnApply
        };
        return _objectSpread(_objectSpread({}, state), {}, {
          open: !state.open,
          filled: action.filled || state.filled
        }, updatedInputs);
      }
    case calculatorActionTypes.SET_INPUT_YIELD:
      return _objectSpread(_objectSpread({}, state), {}, {
        inputYieldValue: action.payload
      });
    case calculatorActionTypes.SET_INPUT_UNIT:
      return _objectSpread(_objectSpread({}, state), {}, {
        inputUnitValue: action.payload
      });
    case calculatorActionTypes.SET_PRICE_DESCRIPTION:
      return _objectSpread(_objectSpread({}, state), {}, {
        priceDescription: action.payload.description,
        sale_unit_quantity: action.payload.sale_unit_quantity,
        bulk_sale_quantity: action.payload.bulk_sale_quantity,
        unit_quantity_text: action.payload.unit_quantity_text
      });
    case calculatorActionTypes.SET_TOTAL_PRICE:
      return _objectSpread(_objectSpread({}, state), {}, {
        totalPrice: action.payload
      });
    case calculatorActionTypes.SET_CHECKED:
      return _objectSpread(_objectSpread({}, state), {}, {
        isChecked: action.payload
      });
    case calculatorActionTypes.SET_ERROR:
      return _objectSpread(_objectSpread({}, state), {}, {
        isError: action.payload,
        isLocalError: !!action.isLocalError,
        isManualUnitChange: (_action$disableCheck = action.disableCheck) !== null && _action$disableCheck !== void 0 ? _action$disableCheck : state.isManualUnitChange,
        errorMessage: action.errorMessage
      });
    case calculatorActionTypes.SET_MANUAL_UNIT_CHANGE:
      return _objectSpread(_objectSpread({}, state), {}, {
        isManualUnitChange: action.payload
      });
    case calculatorActionTypes.RESET_CONTENT:
      return _objectSpread(_objectSpread({}, calculatorInitialState), {}, {
        totalPrice: state.totalPrice,
        priceDescription: state.priceDescription
      });
    default:
      return state;
  }
};
const useCalculator = props => {
  const {
    inputs,
    footer,
    site_code: siteCode,
    error_operation: errorOperation,
    onSetQuantity,
    onOpenModal,
    tracker,
    tracks,
    item_id: itemId,
    variation_id: variationId
  } = props;
  const [state, dispatch] = useReducer(calculatorReducer, _objectSpread(_objectSpread({}, calculatorInitialState), {}, {
    totalPrice: footer.total_price.value,
    errorMessage: inputs.min_error_message.error_label.text
  }));
  const closeModal = () => {
    dispatch({
      type: calculatorActionTypes.CLOSE_MODAL
    });
    tracker(tracks === null || tracks === void 0 ? void 0 : tracks.close);
  };
  const updatePriceDescription = useCallback((yieldValue, unitValue) => {
    let adjustedYieldValue = typeof yieldValue === 'number' ? yieldValue : Number(yieldValue.replace(',', '.'));
    if (adjustedYieldValue === 0) {
      dispatch({
        type: calculatorActionTypes.SET_ERROR,
        payload: true,
        isLocalError: true,
        errorMessage: inputs.min_error_message.error_label.text
      });
      return;
    }
    let adjustedUnitValue = unitValue;
    let unitText = footer.price_description.singular_unit;
    if (state.isChecked && !state.isManualUnitChange && state.inputYieldValue !== '' && state.inputUnitValue !== '') {
      adjustedYieldValue = Math.round(adjustedYieldValue * 1.1 * 100) / 100;
    }
    const localIsError = adjustedYieldValue < inputs.item_yield;
    if (inputs.input_type === 'SIMPLE_INPUT') {
      adjustedYieldValue = Math.ceil(adjustedYieldValue / inputs.item_yield).toFixed(4) * inputs.item_yield;
      adjustedYieldValue = adjustedYieldValue % 1 === 0 ? adjustedYieldValue : adjustedYieldValue.toFixed(2);
      adjustedUnitValue = adjustedYieldValue;
      adjustedYieldValue = formatYieldValue(adjustedYieldValue, siteCode);
      dispatch({
        type: calculatorActionTypes.SET_PRICE_DESCRIPTION,
        payload: {
          description: "".concat(adjustedYieldValue, " ").concat(unitText),
          sale_unit_quantity: adjustedYieldValue,
          bulk_sale_quantity: adjustedYieldValue
        }
      });
    } else {
      if (adjustedYieldValue > inputs.item_yield) {
        adjustedUnitValue = Math.ceil(parseFloat((adjustedYieldValue / inputs.item_yield).toFixed(4)));
        unitText = footer.price_description.plural_unit;
      } else {
        unitText = footer.price_description.singular_unit;
      }
      const descriptionTemplate = footer.price_description.label_description.text.replace('&nbsp;', '\u00A0');
      const updatedDescription = descriptionTemplate.replace('{measure}', adjustedUnitValue * inputs.item_yield % 1 === 0 ? formatYieldValue(adjustedUnitValue * inputs.item_yield, siteCode) : formatYieldValue((adjustedUnitValue * inputs.item_yield).toFixed(2), siteCode)).replace('{sales}', formatYieldValue(adjustedUnitValue, siteCode)).replace('{unit}', unitText);
      dispatch({
        type: calculatorActionTypes.SET_PRICE_DESCRIPTION,
        payload: {
          description: updatedDescription,
          sale_unit_quantity: adjustedUnitValue,
          bulk_sale_quantity: (adjustedUnitValue * inputs.item_yield).toFixed(2),
          unit_quantity_text: "(".concat(adjustedUnitValue, " ").concat(unitText, ")")
        }
      });
    }
    const updatedTotalPrice = adjustedUnitValue * inputs.price_unit;
    dispatch({
      type: calculatorActionTypes.SET_TOTAL_PRICE,
      payload: updatedTotalPrice
    });
    dispatch({
      type: calculatorActionTypes.SET_ERROR,
      payload: false,
      isLocalError: localIsError,
      errorMessage: inputs.min_error_message.error_label.text
    });
  }, [footer.price_description.singular_unit, footer.price_description.label_description.text, footer.price_description.plural_unit, state.isChecked, state.isManualUnitChange, state.inputYieldValue, state.inputUnitValue, inputs.item_yield, inputs.input_type, inputs.price_unit, inputs.min_error_message.error_label.text, siteCode]);
  const handleInputFocus = useCallback(disableCheck => {
    dispatch({
      type: calculatorActionTypes.SET_MANUAL_UNIT_CHANGE,
      payload: disableCheck
    });
  }, []);
  const handleInputChange = useCallback((setInputAction, otherValueAction, factor, isYield) => event => {
    dispatch({
      type: calculatorActionTypes.SET_MANUAL_UNIT_CHANGE,
      payload: false
    });
    const {
      nativeEvent: {
        inputType
      }
    } = event;
    const {
      value
    } = event.target;
    let sanitizedValue = '';

    // eslint-disable-next-line security/detect-unsafe-regex
    const regex = /^\d{0,4}(?:[,.]\d{0,2})?$/;
    if (!regex.test(value)) {
      if (inputType === 'deleteContentBackward') {
        sanitizedValue = value;
      } else {
        return;
      }
    }
    sanitizedValue = value;
    if (!isYield && sanitizedValue !== '') {
      const numericalValue = parseInt(sanitizedValue, 10);
      if (Number.isNaN(numericalValue)) {
        dispatch({
          type: setInputAction,
          payload: ''
        });
        dispatch({
          type: otherValueAction,
          payload: ''
        });
        dispatch({
          type: calculatorActionTypes.SET_PRICE_DESCRIPTION,
          payload: {
            description: ''
          }
        });
        return;
      }
      dispatch({
        type: setInputAction,
        payload: numericalValue.toString()
      });
      const calculatedYield = numericalValue * factor % 1 === 0 ? numericalValue * factor : (numericalValue * factor).toFixed(2);
      dispatch({
        type: otherValueAction,
        payload: calculatedYield.toString(),
        isYield
      });
      updatePriceDescription(calculatedYield, numericalValue);
    } else {
      dispatch({
        type: setInputAction,
        payload: sanitizedValue
      });
      if (sanitizedValue) {
        const numericalValue = Number(sanitizedValue.replace(',', '.'));
        const calculatedUnits = Math.ceil(numericalValue / factor);
        dispatch({
          type: otherValueAction,
          payload: calculatedUnits.toString()
        });
        updatePriceDescription(numericalValue, calculatedUnits);
      } else {
        dispatch({
          type: otherValueAction,
          payload: ''
        });
        dispatch({
          type: calculatorActionTypes.SET_PRICE_DESCRIPTION,
          payload: {
            description: ''
          }
        });
      }
    }
  }, [updatePriceDescription]);
  const handleYieldChange = handleInputChange(calculatorActionTypes.SET_INPUT_YIELD, calculatorActionTypes.SET_INPUT_UNIT, inputs.item_yield, true, 'inputYield');
  const handleUnitChange = handleInputChange(calculatorActionTypes.SET_INPUT_UNIT, calculatorActionTypes.SET_INPUT_YIELD, inputs.item_yield, false, 'inputUnit');
  useEffect(() => {
    const initialYield = !Number.isNaN(parseFloat(state.inputYieldValue.replace(',', '.'))) ? parseFloat(state.inputYieldValue.replace(',', '.')) : inputs.item_yield || 0;
    const initialUnit = parseInt(state.inputUnitValue, 10) || 1;
    updatePriceDescription(initialYield, initialUnit);
  }, [state.inputYieldValue, state.inputUnitValue, inputs.item_yield, updatePriceDescription]);
  useEffect(() => {
    dispatch({
      type: calculatorActionTypes.RESET_CONTENT
    });
  }, [itemId, variationId]);
  const handleToggleOpen = useCallback(() => {
    if (state.open) {
      tracker(tracks === null || tracks === void 0 ? void 0 : tracks.close);
    } else {
      tracker(tracks === null || tracks === void 0 ? void 0 : tracks.open);
    }
    dispatch({
      type: calculatorActionTypes.TOGGLE_OPEN
    });
    if (onOpenModal) {
      onOpenModal();
    }
  }, [onOpenModal, state.open, tracker, tracks]);
  const handleClose = useCallback(() => {
    onSetQuantity({
      sale_unit_quantity: deformatStringValue(state.sale_unit_quantity),
      bulk_sale_quantity: state.bulk_sale_quantity,
      item_id: itemId,
      variation_id: variationId
    }).then(result => {
      if (result) {
        var _result$stock_error_m, _result$stock_error_m2;
        if (!((_result$stock_error_m = result.stock_error_message) !== null && _result$stock_error_m !== void 0 && (_result$stock_error_m = _result$stock_error_m.error_label) !== null && _result$stock_error_m !== void 0 && _result$stock_error_m.text)) {
          dispatch({
            type: calculatorActionTypes.TOGGLE_OPEN,
            filled: true,
            yieldOnApply: state.inputYieldValue,
            unitOnApply: state.inputUnitValue,
            checkedOnApply: state.isChecked
          });
          tracker(_objectSpread(_objectSpread({}, tracks === null || tracks === void 0 ? void 0 : tracks.apply_success), {}, {
            melidata_event: _objectSpread(_objectSpread({}, tracks === null || tracks === void 0 ? void 0 : tracks.apply_success.melidata_event), {}, {
              event_data: _objectSpread(_objectSpread({}, tracks === null || tracks === void 0 ? void 0 : tracks.apply_success.melidata_event.event_data), {}, {
                waste_input_checked: state.isChecked
              })
            })
          }));
          return;
        }
        dispatch({
          type: calculatorActionTypes.SET_ERROR,
          payload: true,
          isLocalError: false,
          errorMessage: (_result$stock_error_m2 = result.stock_error_message) === null || _result$stock_error_m2 === void 0 || (_result$stock_error_m2 = _result$stock_error_m2.error_label) === null || _result$stock_error_m2 === void 0 ? void 0 : _result$stock_error_m2.text
        });
        tracker(_objectSpread(_objectSpread({}, tracks === null || tracks === void 0 ? void 0 : tracks.apply_error_stock), {}, {
          melidata_event: _objectSpread(_objectSpread({}, tracks === null || tracks === void 0 ? void 0 : tracks.apply_error_stock.melidata_event), {}, {
            event_data: _objectSpread(_objectSpread({}, tracks === null || tracks === void 0 ? void 0 : tracks.apply_error_stock.melidata_event.event_data), {}, {
              waste_input_checked: state.isChecked
            })
          })
        }));
      }
    }).catch(() => {
      dispatch({
        type: calculatorActionTypes.SET_ERROR,
        payload: true,
        isLocalError: false,
        errorMessage: errorOperation
      });
    });
  }, [state, onSetQuantity, errorOperation, tracker, tracks, itemId, variationId]);
  const handleCheckboxChange = useCallback(event => {
    dispatch({
      type: calculatorActionTypes.SET_CHECKED,
      payload: event.target.checked
    });
    const yieldValue = parseFloat(state.inputYieldValue.replace(',', '.')) || inputs.item_yield || 0;
    const unitValue = parseInt(state.inputUnitValue, 10) || 1;
    updatePriceDescription(yieldValue, unitValue);
  }, [state.inputUnitValue, state.inputYieldValue, inputs.item_yield, updatePriceDescription]);
  return {
    handleCheckboxChange,
    handleToggleOpen,
    handleYieldChange,
    handleInputFocus,
    handleUnitChange,
    handleClose,
    closeModal,
    state
  };
};
const namespace$1 = 'calculator-block-desktop';
const CalculatorDesktop = props => {
  try {
    const {
      showNotSelectedError,
      header,
      inputs,
      footer,
      messages
    } = props;
    /* eslint-disable react-hooks/rules-of-hooks */
    const {
      handleCheckboxChange,
      handleYieldChange,
      handleUnitChange,
      handleToggleOpen,
      handleInputFocus,
      handleClose,
      closeModal,
      state
    } = useCalculator(_objectSpread({}, props));
    /* eslint-enable react-hooks/rules-of-hooks */

    return /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace$1, "__trigger")
    }, void 0, /*#__PURE__*/_jsx(CalculatorContent, {
      modal: true,
      onClose: closeModal,
      open: state.open,
      trigger: /*#__PURE__*/_jsx(Button, {
        hierarchy: "transparent",
        className: classnames("".concat(namespace$1, "__trigger__button"), {
          open: state.open,
          error: showNotSelectedError
        }),
        onClick: handleToggleOpen
      }, void 0, /*#__PURE__*/_jsx(ButtonTriggerContent, {
        header: header,
        open: state.open,
        namespace: namespace$1,
        error: showNotSelectedError,
        isMobile: false
      }))
    }, void 0, /*#__PURE__*/_jsx(CalculatorInputs, {
      inputs: inputs,
      isError: state.isError || state.isLocalError,
      inputYieldValue: state.inputYieldValue,
      handleYieldChange: handleYieldChange,
      handleInputFocus: handleInputFocus,
      inputUnitValue: state.inputUnitValue,
      handleUnitChange: handleUnitChange,
      namespace: namespace$1
    }), /*#__PURE__*/_jsx(ErrorMessage, {
      isError: state.isError || state.isLocalError,
      errorMessage: state.errorMessage,
      namespace: namespace$1
    }), inputs.check && /*#__PURE__*/_jsx("div", {
      className: classnames("".concat(namespace$1, "__check"))
    }, void 0, /*#__PURE__*/_jsx(Checkbox, {
      label: inputs.check.input_title.text,
      onChange: handleCheckboxChange,
      checked: state.isChecked,
      srLabel: inputs.check.input_title.accessibility_text,
      disabled: state.isError && !state.isChecked
    })), /*#__PURE__*/_jsx("div", {
      className: classnames("".concat(namespace$1, "__ui-separator"))
    }), /*#__PURE__*/_jsx(Footer, {
      priceDescription: state.priceDescription,
      totalPrice: state.totalPrice,
      footer: footer,
      inputYieldValue: state.inputYieldValue,
      inputUnitValue: state.inputUnitValue,
      isError: state.isError || state.isLocalError,
      handleClose: handleClose,
      namespace: namespace$1
    })), /*#__PURE__*/_jsx(ErrorMessage, {
      isError: showNotSelectedError,
      errorMessage: header.error_message,
      namespace: namespace$1,
      className: classnames("".concat(namespace$1, "__outside-error"))
    }), (messages === null || messages === void 0 ? void 0 : messages.length) > 0 && (messages === null || messages === void 0 ? void 0 : messages.map(message => /*#__PURE__*/_jsx("div", {
      className: classnames("".concat(namespace$1, "__messages"))
    }, message.text, /*#__PURE__*/_jsx("span", {}, void 0, " ", message.text, " ")))));
  } catch (error) {
    return props === null || props === void 0 ? void 0 : props.runCatchErrorBoundary(error);
  }
};
CalculatorDesktop.defaultProps = {
  runCatchErrorBoundary: () => {}
};
CalculatorDesktop.displayName = 'CalculatorDesktop';
var CloseIcon = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTAuOTM5NDUzIDIuMDAwMTFMNi45NDI4OSA4LjAwMzU1TDAuOTM5NDUzIDE0LjAwN0wyLjAwMDExIDE1LjA2NzZMOC4wMDM1NSA5LjA2NDIxTDE0LjAwMDEgMTUuMDYwOEwxNS4wNjA4IDE0LjAwMDFMOS4wNjQyMSA4LjAwMzU1TDE1LjA2MDggMi4wMDY5OUwxNC4wMDAxIDAuOTQ2MzI2TDguMDAzNTUgNi45NDI4OUwyLjAwMDExIDAuOTM5NDUzTDAuOTM5NDUzIDIuMDAwMTFaIiBmaWxsPSJibGFjayIgZmlsbC1vcGFjaXR5PSIwLjU1Ii8+Cjwvc3ZnPgo=";
var _img;
const namespace = 'calculator-block-mobile';
const CalculatorMobile = props => {
  try {
    /* eslint-disable react-hooks/rules-of-hooks */
    const {
      showNotSelectedError,
      header,
      inputs,
      footer,
      messages
    } = props;
    const {
      handleCheckboxChange,
      handleInputFocus,
      handleUnitChange,
      handleYieldChange,
      handleToggleOpen,
      handleClose,
      closeModal,
      state
    } = useCalculator(_objectSpread({}, props));
    /* eslint-enable react-hooks/rules-of-hooks */

    return /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace, "__trigger")
    }, void 0, /*#__PURE__*/_jsx(Button, {
      hierarchy: "transparent",
      className: classnames("".concat(namespace, "__trigger__button"), {
        open: state.open,
        error: showNotSelectedError
      }),
      onClick: handleToggleOpen
    }, void 0, /*#__PURE__*/_jsx(ButtonTriggerContent, {
      header: header,
      open: state.open,
      namespace: namespace,
      error: showNotSelectedError,
      isMobile: true
    })), /*#__PURE__*/_jsx(BottomSheet, {
      initialHeight: "fit-content",
      align: "",
      dragIndicator: false,
      close: true,
      closeOutside: true,
      className: classnames("".concat(namespace, "__bottom-sheet")),
      onClose: closeModal,
      srLabel: "bs-label",
      open: state.open
    }, void 0, /*#__PURE__*/_jsx("div", {
      className: "content-mobile-wrapper"
    }, void 0, /*#__PURE__*/_jsx("div", {
      className: classnames("content-mobile-wrapper__header-wrapper")
    }, void 0, /*#__PURE__*/_jsx("h2", {}, void 0, " ", header.title.text_modal, " "), /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: handleToggleOpen
    }, void 0, _img || (_img = /*#__PURE__*/_jsx("img", {
      src: CloseIcon,
      alt: "close button"
    }))), header.subtitles.map(subtitle => /*#__PURE__*/_jsx("p", {
      title: subtitle.accessibility_text
    }, subtitle.text, ' ', subtitle.text, ' '))), /*#__PURE__*/_jsx("div", {
      className: "mobile-content"
    }, void 0, /*#__PURE__*/_jsx(CalculatorInputs, {
      inputs: inputs,
      isError: state.isError || state.isLocalError,
      inputYieldValue: state.inputYieldValue,
      handleYieldChange: handleYieldChange,
      handleInputFocus: handleInputFocus,
      inputUnitValue: state.inputUnitValue,
      handleUnitChange: handleUnitChange,
      namespace: namespace,
      isMobile: true
    }), /*#__PURE__*/_jsx(ErrorMessage, {
      isError: state.isError || state.isLocalError,
      errorMessage: state.errorMessage,
      namespace: namespace
    }), inputs.check && /*#__PURE__*/_jsx("div", {
      className: classnames("".concat(namespace, "__check"))
    }, void 0, /*#__PURE__*/_jsx(Checkbox, {
      label: inputs.check.input_title.text,
      onChange: handleCheckboxChange,
      checked: state.isChecked,
      srLabel: inputs.check.input_title.accessibility_text,
      disabled: state.isError && !state.isChecked
    })), /*#__PURE__*/_jsx("div", {
      className: classnames("".concat(namespace, "__ui-separator"))
    }), /*#__PURE__*/_jsx(Footer, {
      priceDescription: state.priceDescription,
      totalPrice: state.totalPrice,
      footer: footer,
      inputYieldValue: state.inputYieldValue,
      inputUnitValue: state.inputUnitValue,
      isError: state.isError || state.isLocalError,
      handleClose: handleClose,
      namespace: namespace
    })))), /*#__PURE__*/_jsx(ErrorMessage, {
      isError: showNotSelectedError,
      errorMessage: header.error_message,
      namespace: namespace,
      className: classnames("".concat(namespace, "__outside-error"))
    }), (messages === null || messages === void 0 ? void 0 : messages.length) > 0 && (messages === null || messages === void 0 ? void 0 : messages.map(message => /*#__PURE__*/_jsx("div", {
      className: classnames("".concat(namespace, "__message"))
    }, message.text, /*#__PURE__*/_jsx("p", {}, void 0, " ", message.text, " ")))));
  } catch (error) {
    return props === null || props === void 0 ? void 0 : props.runCatchErrorBoundary(error);
  }
};
CalculatorMobile.defaultProps = {
  runCatchErrorBoundary: () => {}
};
CalculatorMobile.displayName = 'CalculatorMobile';
export { CalculatorDesktop, CalculatorMobile };
